import Vue, { PluginObject } from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import myFont from '@/nms/assets/RobotoCondensed-Regular-normal.js'

interface LoadFilesOptions {
  store?: any
}

interface IdWith$Id {
  $id: string
}

interface TableColumn {
  title: string
  value: string
  dataKey: string
  translate?: boolean  // Добавляем свойство translate
  filter?: boolean
  type?: string
  width?: string
  actions?: Array<{
    title: string
    tooltip: string
    icon: string
    color?: string
    emit: string
  }>
}

interface TableRow {
  [key: string]: any
}

interface TableData {
  rows: TableRow[]
  columns?: TableColumn[] // Массив объектов с описанием колонок
}

interface DataObject {
  cls?: string
  type?: string
  name?: string
  [key: string]: any
}

interface PDFOptions {
  orientation?: 'portrait' | 'landscape'
  unit?: 'pt' | 'mm' | 'cm' | 'in' | 'px'
  format?: string | number[]
  font?: string
  fontData?: string
}

type TranslateFunction = (key: string) => string

interface CsvExportOptions {
    delimiter?: string // разделитель, по умолчанию ';'
    dateFormat?: string // формат даты
    includeBom?: boolean // добавлять BOM, по умолчанию true
    columns?: string[] // специфичные колонки (переопределяют стандартные)
}

type IdType = string | IdWith$Id | IdWith$Id[]

const plugin: PluginObject<LoadFilesOptions> = {
  install(VueInstance: typeof Vue, options?: LoadFilesOptions) {
    const store = options?.store

    VueInstance.prototype.$loadFiles = async function(
      id: IdType,
      caller?: any
    ): Promise<any[]> {
      try {
        let $id: string | undefined

        if (typeof id === 'string') {
          $id = id
        } else if (id && typeof id === 'object' && '$id' in id) {
          $id = id.$id
        } else if (Array.isArray(id) && id.length && id[0]?.$id) {
          $id = id[0].$id
        }

        if (!$id) return []

        const obj$ = await (this as any).$bridge.getObjectById($id)
        
        if (obj$) {
          const files$ = await obj$.documents()
          
          if (files$) {
            return files$
          } else {
            console.log((this as any).$options?.name, 'loadFiles', 'SMTH WRONG', files$)
            return []
          }
        }
        
        return []
      } catch (err) {
        console.error('CATCH', '$loadFiles', err)
        return []
      }
    }

    VueInstance.prototype.$csvExport = function(
      this: Vue,
      table: TableData,
      name: string,
      caller?: any,
      exportOptions?: CsvExportOptions
    ): void {
      if (!table || !table.rows || !table.rows.length) return
    
      const options: Required<CsvExportOptions> = {
        delimiter: exportOptions?.delimiter || ';',
        dateFormat: exportOptions?.dateFormat || 'DD.MM.YYYY',
        includeBom: exportOptions?.includeBom !== false,
        columns: exportOptions?.columns || []
      }
    
      const firstRow = table.rows[0]
      
      // Определяем колонки для экспорта с новым приоритетом:
      // 1. Сначала проверяем table.columns (массив объектов с полем value)
      // 2. Если его нет или он пустой, используем options.columns
      // 3. Если и options.columns пустой, используем стандартный набор колонок
      let cols$: string[]
      
      if (table.columns && Array.isArray(table.columns) && table.columns.length) {
        // Извлекаем value из каждого объекта в table.columns
        cols$ = table.columns
          .map(col => col.value)
          .filter(Boolean) // Убираем undefined/null
        console.log('$csvExport: используем колонки из table.columns', cols$)
      } else if (options.columns.length) {
        // Используем колонки из options
        cols$ = options.columns
        console.log('$csvExport: используем колонки из options.columns', cols$)
      } else {
        // Используем стандартный набор колонок
        cols$ = Object.keys(firstRow).filter(i =>
          i === 'maintenance_company_name' ||
          i === 'place' ||
          i === 'position' ||
          i === 'device' ||
          i === 'serial_number' ||
          i === 'device_type' ||
          i === 'warranty_expires' ||
          i === 'maintenance_date' ||
          i === 'life_cycle_expires'
        )
        console.log('$csvExport: используем стандартные колонки', cols$)
      }
    
      // Дополнительная проверка: фильтруем только существующие колонки в данных
      const existingColumns = Object.keys(firstRow)
      cols$ = cols$.filter(col => existingColumns.includes(col))
      
      if (!cols$.length) {
        console.warn('$csvExport: нет доступных колонок для экспорта')
        return
      }
    
      // Получаем функцию для перевода
      const $getTranslate = (this as any).$getTranslate || ((key: string) => key)
      
      // Для заголовков используем title из table.columns, если доступно
      let headers: string[]
      if (table.columns && Array.isArray(table.columns) && table.columns.length) {
        // Берем заголовки из table.columns (title)
        headers = table.columns
          .filter(col => cols$.includes(col.value)) // Берем только те колонки, которые попали в финальный список
          .map(col => $getTranslate(col.title))
      } else {
        // Используем перевод value как заголовок
        headers = cols$.map(col => $getTranslate(col))
      }
      
      // Формируем строки CSV
      const rows = table.rows.map((row: TableRow) => {
        const rowData: string[] = []
        cols$.forEach(col => {
          const value = row[col]
          const isDateColumn = col === 'warranty_expires' || col === 'maintenance_date' || col === 'life_cycle_expires'
          
          if (value && isDateColumn) {
            const date = value as Date
            // Форматирование даты в зависимости от опций
            const formattedDate = date.toISOString().substring(0, 10).split('-').reverse().join('.')
            rowData.push(formattedDate)
          } else if (value !== undefined && value !== null) {
            const translatedValue = $getTranslate(String(value))
            // Экранирование значений, содержащих разделитель или кавычки
            const escapedValue = translatedValue.includes(options.delimiter) || translatedValue.includes('"')
              ? `"${translatedValue.replace(/"/g, '""')}"`
              : translatedValue
            rowData.push(escapedValue)
          } else {
            // Пустое значение
            rowData.push('')
          }
        })
        return rowData.join(options.delimiter)
      })
    
      // Собираем весь CSV контент
      const csvLines = [headers.join(options.delimiter), ...rows]
      let csvContent = csvLines.join('\n')
      
      // Добавляем BOM если нужно
      if (options.includeBom) {
        csvContent = '\uFEFF' + csvContent
      }
      
      // Создаем Blob и скачиваем
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      
      const moment = (this as any).$moment
      const dateStr = moment ? moment().format('DD-MM-YY') : new Date().toISOString().substring(0, 10).replace(/-/g, '-')
      const fileName = `${name}_${dateStr}.csv`
      
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
    }

    VueInstance.prototype.$generatePDF = function(
      this: Vue,
      table: TableData,
      data: DataObject,
      caller?: any,
      pdfOptions?: PDFOptions
    ): void {
      if (!table || !table.rows || !table.rows.length) {
        console.warn('$generatePDF: нет данных для генерации PDF')
        return
      }
      console.log('$generatePDF: ', table)
      // Опции по умолчанию
      const options = {
        orientation: pdfOptions?.orientation || 'portrait',
        unit: pdfOptions?.unit || 'in',
        format: pdfOptions?.format || 'letter',
        font: pdfOptions?.font || 'MyFont',
        fontData: pdfOptions?.fontData || myFont // myFont должен быть определен где-то выше
      }
    
      // Подготавливаем колонки с переводом заголовков
      const columns = (table.columns || []).map(col => ({
        ...col,
        title: (this as any).$getTranslate(col.title)
      }))
    
      // Создаем документ PDF
      const doc = new jsPDF({
        orientation: options.orientation as 'portrait' | 'landscape',
        unit: options.unit as 'pt' | 'mm' | 'cm' | 'in' | 'px',
        format: options.format
      })
    
      // Добавляем шрифт (если передан)
      if (options.fontData) {
        doc.addFileToVFS('RobotoCondensed-Regular.ttf', options.fontData)
        doc.addFont('RobotoCondensed-Regular.ttf', options.font, 'normal')
        doc.setFont(options.font)
      }
    
      // Формируем заголовок документа
      const getTranslate = (this as any).$getTranslate || ((key: string) => key)
      const dataCls = (this as any).$getTranslate(data.cls || '')
      const dataType = (this as any).$getTranslate(data.type || '')
      const dataName = (this as any).$getTranslate(data.name || '')
      
      const title = `${dataCls} - ${dataType} "${dataName}"`
      
      doc.setFontSize(16)
        .text(title, 0.5, 1.0)
        .setLineWidth(0.01)
        .line(0.5, 1.1, 8.0, 1.1)
    
      // Форматируем данные для таблицы
      const formattedRows = (this as any).$getFormattedDataForPDF ? 
        (this as any).$getFormattedDataForPDF(table.rows, table.columns) : 
        table.rows
    
      // Создаем таблицу
      autoTable(doc, {
        styles: { font: options.font as any },
        columnStyles: { 4: { minCellWidth: 1 } },
        columns: columns as any,
        body: formattedRows,
        margin: { left: 0.5, top: 1.25 }
      })
    
      // Добавляем подвал
      const moment = (this as any).$moment
      const dateStr = moment ? moment().format('DD MM YYYY HH:mm') : new Date().toISOString().substring(0, 16).replace('T', ' ')
      const userLogin = (this as any).$store?.state?.auth?.user?.login || 'unknown'
      
      doc.setFontSize(11)
        .setTextColor(0, 0, 255)
        .text(
          `${(this as any).$getTranslate('this report was generated on')} ${dateStr} / ${userLogin}`,
          0.5,
          doc.internal.pageSize.height - 0.5
        )
    
      // Сохраняем PDF
      const fileName = `${data.name || 'document'}_configuration.pdf`
      doc.save(fileName)
    }

    VueInstance.prototype.$getFormattedDataForPDF = function(
      this: Vue,
      rows: TableRow[],
      columns?: TableColumn[]  // Добавляем параметр columns
    ): any[] {
      if (!rows || !rows.length) return []
      return rows.map(row => {
        const formattedRow: any = {}
        
        // Если есть columns, используем их для определения необходимости перевода
        if (columns && columns.length) {
          columns.forEach(col => {
            const value = row[col.value]
            const shouldTranslate = col.translate === true
            
            
            if (value && shouldTranslate) {
              formattedRow[col.value] = (this as any).$getTranslate(String(value))
            } else if (value && (col.type === 'timestamp' || value instanceof Date)) {
              const date = value instanceof Date ? value : new Date(value)
              if (!isNaN(date.getTime())) {
                formattedRow[col.value] = date.toISOString().substring(0, 10).split('-').reverse().join('.')
              } else {
                formattedRow[col.value] = value
              }
            } else if (value && typeof value === 'object' && value.$id) {
              formattedRow[col.value] = value.full_name || value.name || value.$id
            } else if (value !== undefined && value !== null) {
              formattedRow[col.value] = value
            } else {
              formattedRow[col.value] = ''
            }
          })
        } else {
          // Старая логика без columns
          Object.keys(row).forEach(key => {
            const value = row[key]
            
            if (value instanceof Date) {
              formattedRow[key] = value.toISOString().substring(0, 10).split('-').reverse().join('.')
            } else if (value && typeof value === 'object' && value.$id) {
              formattedRow[key] = value.full_name || value.name || value.$id
            } else {
              formattedRow[key] = value
            }
          })
        }
        
        return formattedRow
      })
    }

    VueInstance.prototype.$csvExport__ = function(
        this: Vue,
        table: TableData,
        name: string,
        caller?: any,
        exportOptions?: CsvExportOptions
      ): void {
        if (!table || !table.rows || !table.rows.length) return
  
        const options: Required<CsvExportOptions> = {
          delimiter: exportOptions?.delimiter || ';',
          dateFormat: exportOptions?.dateFormat || 'DD.MM.YYYY',
          includeBom: exportOptions?.includeBom !== false,
          columns: exportOptions?.columns || []
        }
  
        const firstRow = table.rows[0]
        
        // Определяем колонки для экспорта
        let cols$: string[]
        if (options.columns.length) {
          cols$ = options.columns
        } else {
          cols$ = Object.keys(firstRow).filter(i =>
            i === 'maintenance_company_name' ||
            i === 'place' ||
            i === 'position' ||
            i === 'device' ||
            i === 'serial_number' ||
            i === 'device_type' ||
            i === 'warranty_expires' ||
            i === 'maintenance_date' ||
            i === 'life_cycle_expires'
          )
        }
  
        // Получаем функцию для перевода
        const $getTranslate = (this as any).$getTranslate || ((key: string) => key)
        
        // Получаем переведенные заголовки
        const headers = cols$.map(col => $getTranslate(col))
        
        // Формируем строки CSV
        const rows = table.rows.map((row: TableRow) => {
          const rowData: string[] = []
          cols$.forEach(col => {
            const value = row[col]
            const isDateColumn = col === 'warranty_expires' || col === 'maintenance_date' || col === 'life_cycle_expires'
            
            if (value && isDateColumn) {
              const date = value as Date
              // Форматирование даты в зависимости от опций
              const formattedDate = date.toISOString().substring(0, 10).split('-').reverse().join('.')
              rowData.push(formattedDate)
            } else {
              const translatedValue = $getTranslate(String(value))
              // Экранирование значений, содержащих разделитель или кавычки
              const escapedValue = translatedValue.includes(options.delimiter) || translatedValue.includes('"')
                ? `"${translatedValue.replace(/"/g, '""')}"`
                : translatedValue
              rowData.push(escapedValue)
            }
          })
          return rowData.join(options.delimiter)
        })
  
        // Собираем весь CSV контент
        const csvLines = [headers.join(options.delimiter), ...rows]
        let csvContent = csvLines.join('\n')
        
        // Добавляем BOM если нужно
        if (options.includeBom) {
          csvContent = '\uFEFF' + csvContent
        }
        
        // Создаем Blob и скачиваем
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        
        const moment = (this as any).$moment
        const dateStr = moment ? moment().format('DD-MM-YY') : new Date().toISOString().substring(0, 10).replace(/-/g, '-')
        const fileName = `${name}_${dateStr}.csv`
        
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        URL.revokeObjectURL(url)
      }
  }
}

export default plugin

// Расширение типов для Vue
declare module 'vue/types/vue' {
  interface Vue {
    $loadFiles: (id: IdType, caller?: any) => Promise<any[]>
    $csvExport: (table: TableData, name: string, caller?: any, options?: CsvExportOptions) => void
    $generatePDF: (table: TableData, data: DataObject, caller?: any, pdfOptions?: PDFOptions) => void
    $getFormattedDataForPDF?: (rows: TableRow[]) => any[]
  }
}